import { Card, Stack, Typography, Box, useTheme } from '@mui/material';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SearchGrid } from '@/components/SearchGrid';
import { Alert } from '@/components/shared/Alert';
import { Dropdown, DropdownType } from '@/components/shared/Dropdown';
import {
    FONT_WEIGHT,
    SMBold,
    MDRegular,
    TextMDSemiBold,
    TextSMRegular
} from '@/typography/Typography';
import { useEffect, useRef, useState } from 'react';
import {
    useGetPOSLocationsAndDepartments,
    useTipsHistory,
    useTipsHistoryExcelDownload
} from '@/services/paymentsQuery';
import { useTipsFilters } from './useTipsFilters';
import CardHeader from '@/components/shared/CardHeader';
import Chip from '@/components/shared/Chip';
import { TipsLocationList } from './TipsLocationList';
import {
    TablePagination,
    usePagination
} from '@/components/shared/TablePagination';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { useMutation } from '@tanstack/react-query';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import {
    PrimaryButton,
    SecondaryButtonRegular,
    OutlinedDangerButton
} from '@/components/shared/Button';
import { downloadExcel } from '@/utils/excelExport';
import { useUser } from '@/contexts/user/UserContext';
import { usePOSProvider } from '@/utils/providers';
import { AddManualJournalDialog } from '@/pages/company/tips/AddManualJournalDialog';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { ConfirmationOverlay } from '@/pages/company/disable/ConfirmationOverlay';
import { RESYNC_FROM_POS_ELIGIBLE_PROVIDERS } from '@/utils/constant';
import { DATE_FORMAT } from '@/lib/constants';
import { isResyncEligible, tipsRowKey } from './tipsResync';
import { TipsHistory, TipsResyncRowResult } from '@/services/PaymentsServiceV2';

const paymentsService = PaymentsAPIServiceV2.getInstance();

export const TipsListPage = () => {
    const theme = useTheme();
    const { company } = useUser();
    const { posProviderName } = usePOSProvider();
    const isManual = posProviderName === 'Manual';
    const [showAddJournal, setShowAddJournal] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(
        new Set()
    );
    const {
        locationName,
        statusFilter,
        reportingStatusFilter,
        setLocationName,
        setStatusFilter,
        setReportingStatusFilter,
        filtersKey
    } = useTipsFilters();
    const {
        data,
        isPending: locationPending,
        error: locationError
    } = useGetPOSLocationsAndDepartments();

    useHandleResponse({
        error: locationError
    });

    const { page, resetPage, rowsPerPage, setRowsPerPage } = usePagination();
    const prevFiltersKeyRef = useRef(filtersKey);

    // Reset page when filters actually change (skip initial mount and StrictMode re-runs)
    useEffect(() => {
        if (prevFiltersKeyRef.current === filtersKey) {
            return;
        }
        prevFiltersKeyRef.current = filtersKey;
        resetPage();
    }, [filtersKey]);

    const {
        data: tipsHistoryData,
        isPending: historyPending,
        error: historyError
    } = useTipsHistory(
        page * rowsPerPage,
        rowsPerPage,
        locationName === 'all' ? [] : [locationName],
        statusFilter === 'all' ? [] : [statusFilter],
        reportingStatusFilter === 'all' ? [] : [reportingStatusFilter]
    );

    useHandleResponse({
        error: historyError
    });

    const tipsHistory = tipsHistoryData?.data?.tipsHistory ?? [];

    const canResyncFromPos =
        !!posProviderName &&
        RESYNC_FROM_POS_ELIGIBLE_PROVIDERS.includes(posProviderName);

    const [showResyncConfirm, setShowResyncConfirm] = useState(false);
    const [resyncResults, setResyncResults] = useState<
        TipsResyncRowResult[] | null
    >(null);
    // Captured when the resync is confirmed: the results come back keyed by location and
    // date, and the refetched table they land next to may no longer hold those rows.
    const [resyncRowLabels, setResyncRowLabels] = useState<
        Record<string, string>
    >({});

    // Intersecting the ticked keys with the rows actually on screen is what keeps a key
    // left over from an earlier filter or page out of the resync request.
    const eligibleRows = tipsHistory.filter(isResyncEligible);
    const selectedRows = eligibleRows.filter((tip) =>
        selectedRowKeys.has(tipsRowKey(tip))
    );
    const allEligibleSelected =
        eligibleRows.length > 0 && selectedRows.length === eligibleRows.length;

    const toggleRow = (tip: TipsHistory) => {
        const key = tipsRowKey(tip);
        setSelectedRowKeys((current) => {
            const next = new Set(current);
            if (!next.delete(key)) next.add(key);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelectedRowKeys(
            allEligibleSelected
                ? new Set()
                : new Set(eligibleRows.map(tipsRowKey))
        );
    };

    const rowLabel = (tip: TipsHistory) =>
        `${tip.locationName} — ${tip.date?.format(DATE_FORMAT.DAY_MONTH_YEAR)}`;

    const { resyncTipsFromPos } = usePaymentsMutations();

    useHandleResponse({
        error: resyncTipsFromPos.error,
        isPending: resyncTipsFromPos.isPending,
        isSuccess: resyncTipsFromPos.isSuccess,
        isSuccessCallBack: () => {
            setResyncResults(resyncTipsFromPos.data?.data?.results ?? []);
            setSelectedRowKeys(new Set());
            setShowResyncConfirm(false);
        }
    });

    const confirmResync = () => {
        setResyncRowLabels(
            Object.fromEntries(
                selectedRows.map((tip) => [tipsRowKey(tip), rowLabel(tip)])
            )
        );

        resyncTipsFromPos.mutate(
            selectedRows.map((tip) => ({
                locationId: tip.locationId,
                date: tip.date?.format(DATE_FORMAT.SERVER_DATE)
            }))
        );
    };

    const retryMutation = useMutation({
        mutationFn: () => paymentsService.retryTipsReport()
    });

    useHandleResponse({
        error: retryMutation.error,
        isPending: retryMutation.isPending
    });

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(setLoader(locationPending || historyPending));
    }, [locationPending, historyPending]);

    const {
        data: excelData,
        refetch: refetchExcel,
        error: excelError,
        isSuccess: isExcelSuccess,
        isPending: _excelPending
    } = useTipsHistoryExcelDownload(
        locationName === 'all' ? [] : [locationName],
        statusFilter === 'all' ? [] : [statusFilter],
        reportingStatusFilter === 'all' ? [] : [reportingStatusFilter],
        false
    );

    const downloadLock = useRef(true);

    useHandleResponse({
        error: excelError
    });

    useEffect(() => {
        if (isExcelSuccess && !downloadLock.current) {
            downloadLock.current = true;
            const rows = excelData?.data?.tipsHistory ?? [];
            const fileName = `Tips_History_${
                new Date().toISOString().split('T')[0]
            }.xlsx`;
            downloadExcel(rows, 'Tips History', fileName, {
                ignoreFields: ['posJournalAmounts'],
                timezone: company?.ianaTimeZone
            });
        }
    }, [excelData, isExcelSuccess]);

    const resetFilters = () => {
        setLocationName('all');
        setStatusFilter('all');
        setReportingStatusFilter('all');
        resetPage();
    };

    const actions = (
        <Stack direction="row" gap={'8px'}>
            {isManual && (
                <PrimaryButton
                    onClick={() => setShowAddJournal(true)}
                    size="small"
                    sx={{ borderRadius: '5px' }}
                >
                    {t('tips.add_journal')}
                </PrimaryButton>
            )}
            {canResyncFromPos && (
                <PrimaryButton
                    onClick={() => setShowResyncConfirm(true)}
                    disabled={selectedRows.length === 0}
                    size="small"
                    sx={{ borderRadius: '5px' }}
                >
                    {t('tips.resync_from_pos')}
                </PrimaryButton>
            )}
            <SecondaryButtonRegular
                onClick={() => {
                    downloadLock.current = false;
                    refetchExcel();
                }}
                size="small"
                sx={{ borderRadius: '5px' }}
            >
                {t('download_as_excel')}
            </SecondaryButtonRegular>
        </Stack>
    );

    const resyncOutcomeLabel = (result: TipsResyncRowResult) => {
        if (result.outcome === 'SkippedAlreadyReported')
            return t('tips.resync_outcome_skipped_reported');
        if (result.outcome === 'Failed') return t('tips.resync_outcome_failed');
        return t('tips.resync_outcome_succeeded');
    };

    const resyncSummary = (() => {
        if (!resyncResults) return null;

        const counts = {
            succeeded: resyncResults.filter((r) => r.outcome === 'Succeeded')
                .length,
            skipped: resyncResults.filter(
                (r) => r.outcome === 'SkippedAlreadyReported'
            ).length,
            failed: resyncResults.filter((r) => r.outcome === 'Failed').length
        };

        return {
            counts,
            severity:
                counts.failed > 0
                    ? ('error' as const)
                    : counts.skipped > 0
                      ? ('warning' as const)
                      : ('success' as const),
            // Only the days that did not go through need explaining row by row.
            unresolved: resyncResults.filter((r) => r.outcome !== 'Succeeded')
        };
    })();

    return (
        <div>
            <MDRegular sx={{ mb: 3, color: '#667085' }}>
                {t('tips.tips_daily_subtitle')}
            </MDRegular>

            {resyncSummary && (
                <Alert
                    severity={resyncSummary.severity}
                    onClose={() => setResyncResults(null)}
                    // The shared Alert is one line tall; a per-day breakdown needs room.
                    sx={{ maxHeight: 'none', py: 1.5 }}
                    message={
                        <Box>
                            <strong>
                                {t('tips.resync_summary', resyncSummary.counts)}
                            </strong>
                            {resyncSummary.unresolved.map((result) => {
                                const key = `${result.locationId}::${result.date?.format(
                                    DATE_FORMAT.SERVER_DATE
                                )}`;

                                return (
                                    <Box key={key}>
                                        {`${resyncRowLabels[key] ?? key}: ${
                                            result.reason ??
                                            resyncOutcomeLabel(result)
                                        }`}
                                    </Box>
                                );
                            })}
                        </Box>
                    }
                />
            )}

            {(tipsHistoryData?.data
                ?.unreportedLocationsInPreviousPayrollsCount ?? 0) > 0 && (
                <Alert
                    severity="error"
                    message={
                        <Box sx={{ color: '#D92D20' }}>
                            <strong>
                                {t('tips.unreported_locations_warning_title', {
                                    settlementsCount:
                                        tipsHistoryData?.data
                                            ?.unreportedSettlementsInPreviousPayrollsCount ??
                                        0,
                                    locationsCount:
                                        tipsHistoryData?.data
                                            ?.unreportedLocationsInPreviousPayrollsCount ??
                                        0
                                })}
                            </strong>{' '}
                            {t('tips.unreported_locations_warning_message')}
                        </Box>
                    }
                    action={
                        <OutlinedDangerButton
                            onClick={() => retryMutation.mutate()}
                            disabled={retryMutation.isPending}
                            size="small"
                            loading={retryMutation.isPending}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {retryMutation.isSuccess ? (
                                <>
                                    <CheckCircleIcon
                                        sx={{
                                            fontSize: 16,
                                            mr: 1,
                                            color: 'success.main'
                                        }}
                                    />
                                    {t('request_initiated')}
                                </>
                            ) : (
                                !retryMutation.isPending && t('retry_now')
                            )}
                        </OutlinedDangerButton>
                    }
                />
            )}

            {isManual && (
                <Alert
                    severity="info"
                    message={
                        // Single inline wrapper keeps the Trans output as one flex
                        // item inside the Alert's flex message container; otherwise
                        // flexbox trims the whitespace between "under" and the bold
                        // span, gluing the words together.
                        <Box component="span">
                            <Trans
                                i18nKey="tips.manual_mode_info"
                                components={{
                                    strong: (
                                        <Box
                                            component="span"
                                            sx={{
                                                fontWeight:
                                                    FONT_WEIGHT.SEMI_BOLD
                                            }}
                                        />
                                    )
                                }}
                            />
                        </Box>
                    }
                    // The shared Alert caps height at 40px (one line); lift it and
                    // add padding so the two-line manual notice isn't cramped.
                    sx={{ maxHeight: 'none', py: 1.5 }}
                />
            )}

            <SearchGrid container justifyContent={'space-between'}>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    gap={1}
                    justifyContent={'space-between'}
                >
                    <div>
                        <SMBold noWrap={true}>{t('filter_by')}</SMBold>
                    </div>
                    <Dropdown
                        style={{ width: '400px' }}
                        choices={[
                            {
                                label: t('tips.show_all_locations'),
                                value: 'all'
                            },
                            ...(data?.data?.locations?.map((filter) => ({
                                label: filter.locationName,
                                value: filter.locationId
                            })) || [])
                        ]}
                        label=""
                        value={locationName}
                        type={DropdownType.SingleShowTitle}
                        onChange={(val) => {
                            setLocationName(val);
                        }}
                    />
                    <Dropdown
                        style={{ width: '250px' }}
                        choices={[
                            {
                                label: t('tips.show_all_statuses'),
                                value: 'all'
                            },
                            {
                                label: t('tips.status_awaiting'),
                                value: 'Awaiting'
                            },
                            {
                                label: t('tips.status_succeeded'),
                                value: 'Succeeded'
                            }
                        ]}
                        label=""
                        value={statusFilter}
                        type={DropdownType.SingleShowTitle}
                        onChange={(val) => {
                            setStatusFilter(val);
                        }}
                    />
                    <Dropdown
                        style={{ width: '250px' }}
                        choices={[
                            {
                                label: t('tips.show_all_reporting_statuses'),
                                value: 'all'
                            },
                            {
                                label: t('tips.reporting_status_not_reported'),
                                value: 'NotReported'
                            },
                            {
                                label: t(
                                    'tips.reporting_status_partially_reported'
                                ),
                                value: 'PartiallyReported'
                            },
                            {
                                label: t(
                                    'tips.reporting_status_fully_reported'
                                ),
                                value: 'FullyReported'
                            },
                            {
                                label: t('tips.check_all_issues'),
                                value: 'AllIssues',
                                labelStyle: { color: theme.palette.error.main },
                                itemStyle: { color: theme.palette.error.main }
                            }
                        ]}
                        label=""
                        value={reportingStatusFilter}
                        type={DropdownType.SingleShowTitle}
                        onChange={(val) => {
                            setReportingStatusFilter(val);
                        }}
                    />
                    <PrimaryButton onClick={resetFilters}>
                        <Typography
                            fontSize={'14px'}
                            lineHeight={'20px'}
                            fontWeight={600}
                            noWrap
                        >
                            {t('reset_filter')}
                        </Typography>
                    </PrimaryButton>
                </Stack>
            </SearchGrid>

            <Card>
                <CardHeader
                    label={t('tips.daily_settlements')}
                    chip={
                        <Chip
                            icon={<span></span>}
                            label={
                                <Typography fontWeight={FONT_WEIGHT.SEMI_BOLD}>
                                    {t('filtered_of_total', {
                                        filtered:
                                            tipsHistoryData?.data
                                                ?.filteredTotal ?? 0,
                                        total: tipsHistoryData?.data?.total ?? 0
                                    })}
                                </Typography>
                            }
                            color={'success'}
                        />
                    }
                    actions={actions}
                />
                <TipsLocationList
                    items={tipsHistory}
                    reloadTransactions={() => {}}
                    allowJournalDelete={isManual}
                    enableRowSelection={canResyncFromPos}
                    selectedRowKeys={selectedRowKeys}
                    onToggleRow={toggleRow}
                    onToggleSelectAll={toggleSelectAll}
                    allEligibleSelected={allEligibleSelected}
                ></TipsLocationList>
                {(tipsHistoryData?.data?.tipsHistory?.length ?? 0) == 0 && (
                    <Typography textAlign={'center'}>
                        {t('no_records')}
                    </Typography>
                )}
                <TablePagination
                    page={page}
                    rowsPerPageOptions={[
                        {
                            label: '16',
                            value: 16
                        },
                        {
                            label: '24',
                            value: 24
                        },
                        {
                            label: '32',
                            value: 32
                        },
                        {
                            label: 'All',
                            value: -1
                        }
                    ]}
                    count={tipsHistoryData?.data?.total ?? 0}
                    rowsPerPage={rowsPerPage}
                    resetPage={resetPage}
                    onPageChange={() => {}}
                    onRowsPerPageChange={(newRowsPerPage) => {
                        setRowsPerPage(newRowsPerPage as number);
                    }}
                />
            </Card>

            {isManual && (
                <AddManualJournalDialog
                    open={showAddJournal}
                    onClose={() => setShowAddJournal(false)}
                    locations={data?.data?.locations ?? []}
                />
            )}

            <ConfirmationOverlay
                open={showResyncConfirm}
                loading={resyncTipsFromPos.isPending}
                onCancel={() => setShowResyncConfirm(false)}
                onContinue={confirmResync}
                continueText={t('tips.resync_confirm_continue')}
                displayAreYouSureText={false}
                p1={
                    <TextMDSemiBold className="text-center">
                        {t('tips.resync_confirm_title')}
                    </TextMDSemiBold>
                }
                p2={
                    <TextSMRegular
                        mb={2}
                        color={theme.palette.text.muted}
                        className="text-center"
                    >
                        {t('tips.resync_confirm_warning')}
                    </TextSMRegular>
                }
            />
        </div>
    );
};
